import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Loader2 } from 'lucide-react'
import { setAccessToken } from '@adthlete/shared-frontend'
import api from '@/lib/api'
import AuthCard from '@/components/AuthCard'
import { fetchCurrentUser } from '../redux/userSlice'
import type { AppDispatch } from '../store'

const inputClass =
  'h-12 w-full rounded-xl border border-[#D8DEE4] px-4 text-[#0F172A] outline-none focus:border-[#3F7A0A]'

export default function ResetPassword() {
  const token = useSearchParams()[0].get('token')
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [email, setEmail] = useState('')
  const [valid, setValid] = useState<boolean | null>(token ? null : false)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(token ? '' : 'This reset link is invalid.')

  useEffect(() => {
    if (!token) return
    api
      .post('/users/validate-reset-token', { token })
      .then(({ data }) => {
        setEmail(data.user?.email || '')
        setValid(true)
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'This reset link is invalid or has expired.')
        setValid(false)
      })
  }, [token])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (password.length < 6) return setError('Password must be at least 6 characters long.')
    if (password !== confirm) return setError('Passwords do not match.')
    setBusy(true)
    try {
      const { data } = await api.post('/users/reset-password', { token, password })
      // The reset also signs this device in: keep the access token in memory, load the profile
      setAccessToken(data.token)
      await dispatch(fetchCurrentUser())
      navigate('/', { replace: true })
    } catch (err: any) {
      setError(err.response?.data?.error || "Couldn't reset your password. Please try again.")
      setBusy(false)
    }
  }

  if (valid === null) {
    return (
      <AuthCard title="Reset your password">
        <p className="text-center text-[#4B5563]">Checking your link…</p>
      </AuthCard>
    )
  }

  if (!valid) {
    return (
      <AuthCard title="Link expired">
        <p className="mb-4 text-center text-[#4B5563]">{error}</p>
        <Link
          to="/forgot-password"
          className="block text-center text-sm font-medium text-[#3F7A0A] underline underline-offset-2"
        >
          Send me a new link
        </Link>
      </AuthCard>
    )
  }

  return (
    <AuthCard title="Set a new password">
      <form onSubmit={submit} className="space-y-4">
        {email && <p className="text-center text-sm text-[#4B5563]">For {email}</p>}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <input
          type="password"
          required
          minLength={6}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password"
          className={inputClass}
        />
        <input
          type="password"
          required
          minLength={6}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Confirm new password"
          className={inputClass}
        />
        <button
          type="submit"
          disabled={busy}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-medium text-black hover:bg-[#a8e45a] disabled:opacity-60"
        >
          {busy && <Loader2 className="h-5 w-5 animate-spin" />}
          Reset password
        </button>
      </form>
    </AuthCard>
  )
}
